// Centralized API Service

const API_BASE_URL = 'https://leadapi.crmbytes.com/api/Lead';
const GEOLOCATION_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';

/**
 * Fetch user geolocation data
 */
export const fetchGeoLocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API_URL, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    const data = await response.json();
    
    if (data.isSuccess && data.statusCode === 200) {
      return {
        success: true,
        data: data.data,
        rawResponse: data
      };
    }
    
    return {
      success: false,
      data: null,
      rawResponse: data
    };
  } catch (error) {
    console.error('Geolocation API error:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};

/**
 * Extract country code from country dial code string
 * Example: "+1" -> "1", "+44" -> "44"
 */
const extractDialCode = (dialCode) => {
  if (!dialCode) return '';
  return dialCode.toString().replace(/[^0-9]/g, '');
};

/**
 * Clean phone number to only numeric
 */
const cleanPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.toString().replace(/[^0-9]/g, '');
};

/**
 * Get country code from dial code
 * This is a mapping of common dial codes to country codes
 */
const getCountryCodeFromDialCode = (dialCode) => {
  const dialCodeMap = {
    '1': 'US',
    '44': 'UK',
    '91': 'IN',
    '61': 'AU',
    '81': 'JP',
    '86': 'CN',
    '33': 'FR',
    '49': 'DE',
    '39': 'IT',
    '34': 'ES',
    '7': 'RU',
    '55': 'BR',
    '52': 'MX',
    '82': 'KR',
    '31': 'NL',
    '46': 'SE',
    '47': 'NO',
    '358': 'FI',
    '41': 'CH',
    '32': 'BE'
  };
  
  const numericDialCode = extractDialCode(dialCode);
  return dialCodeMap[numericDialCode] || 'US';
};

/**
 * Submit lead form data
 * @param {Object} formData - Form data object
 * @param {Object} geoData - Geolocation data (optional)
 */
export const submitLead = async (formData, geoData = null) => {
  try {
    const currentPageUrl = window.location.href;
    
    // Extract numeric values
    const numericDialCode = parseInt(extractDialCode(formData.countryCode));
    const numericPhoneNumber = cleanPhoneNumber(formData.contactNumber);
    
    // Get country code
    const countryCode = getCountryCodeFromDialCode(formData.countryCode);
    
    // Build payload
    const payload = {
      name: formData.name || '',
      email: formData.email || '',
      countryDialCode: numericDialCode || 1,
      country: countryCode,
      phoneNumber: numericPhoneNumber,
      ipAddress: geoData?.ipAddress || '',
      description: formData.message || '',
      pageUrl: currentPageUrl,
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? JSON.stringify({
        isSuccess: true,
        data: geoData,
        errorMessage: '',
        statusCode: 200
      }) : '',
      packageTitle: '',
      packageAmount: 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch(`${API_BASE_URL}/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle 200 Success
    if (response.status === 200 && data.isSuccess && data.data?.customerId) {
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message || 'Lead created successfully',
        data: data.data
      };
    }

    // Handle 401 Unauthorized
    if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized access',
        statusCode: 401
      };
    }

    // Handle 400 Bad Request
    if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid request',
        statusCode: 400
      };
    }

    // Handle other errors
    return {
      success: false,
      error: data.errorMessage || 'Failed to submit form',
      statusCode: response.status
    };

  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      error: 'Network error. Please try again.',
      statusCode: 500
    };
  }
};

/**
 * Complete form submission with geolocation
 * This is the main function to call from forms
 */
export const submitFormWithGeoLocation = async (formData) => {
  // First, fetch geolocation
  const geoResult = await fetchGeoLocation();
  
  const geoData = geoResult.success ? geoResult.data : null;
  
  // Submit the lead
  const result = await submitLead(formData, geoData);
  
  return result;
};
